import { useShiftEditor } from '../useShiftEditor'
import { useCompanyInfo } from 'queries/company'
import { Box, Flex, Text } from 'ui'
import { useCompanyPosition } from 'hooks/useCompanyPosition'
import styled from 'styled-components'
import { PaymentTypeEnum } from '../schemas'
import { CalendarIcon, ClockIcon } from 'lucide-react'
import { usePreviewData } from './usePreviewData'
import { format } from 'date-fns'
import { previewTokens } from './styles'
import { OnDemandDeliverySchedule } from './OnDemandDeliverySchedule'
import { resolveEditorBillRate } from '../resolveEditorBillRate'

const ShiftHero = styled(Flex)`
  flex-direction: column;
  align-items: center;
  padding: 10px ${previewTokens.spacing.gutter} 22px;
  color: ${previewTokens.colors.text};
  text-align: center;
`

const CompanyLogo = styled(Box)`
  width: 56px;
  height: 56px;
  flex: 0 0 56px;
  border: 1px solid ${previewTokens.colors.text};
  border-radius: 50%;
  background-color: ${previewTokens.colors.surface};
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
`

const PositionName = styled(Text)`
  margin: 12px 0 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
`

const WorkplaceName = styled(Text)`
  margin: 4px 0 0;
  color: ${previewTokens.colors.muted};
  font-size: 13px;
  line-height: 18px;
`

const ShiftPay = styled(Text)`
  margin: 18px 0 0;
  font-size: 24px;
  font-weight: 600;
  line-height: 28px;
`

const EstimatedPay = styled(Flex)`
  align-items: center;
  gap: 5px;
  margin-top: 5px;
`

const EstimatedPayText = styled(Text)`
  margin: 0;
  color: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
`

const DateTimePill = styled(Flex)`
  box-sizing: border-box;
  height: 48px;
  min-height: 48px;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 20px;
  padding: 0 14px;
  border: 1px solid ${previewTokens.colors.border};
  border-radius: 999px;
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
  font-weight: 500;
`

const DateTimeText = styled(Text)`
  margin: 0;
  color: inherit;
  font-size: inherit;
  line-height: inherit;
`

const EmploymentBadge = styled(Text)`
  margin: 10px 0 0;
  padding: 4px 10px;
  border: 1px solid ${previewTokens.colors.border};
  border-radius: 999px;
  background: ${previewTokens.colors.badge};
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
`

const previewAmountFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

const formatPreviewAmount = (amount: number) =>
  `$${previewAmountFormatter.format(amount)}`

export const ShiftHeader = () => {
  const { shiftData, isOnDemandDeliveryBlock, isNew, editData } =
    useShiftEditor()
  const { data: company } = useCompanyInfo()
  const position = useCompanyPosition(shiftData?.details?.positionId)
  const { estimatedPay } = usePreviewData()

  if (!shiftData && !company) return null

  const isHourly = shiftData.payment?.paymentType === PaymentTypeEnum.Hourly
  const pay = shiftData.payment?.pay
  const originalPositionId = editData?.position?.id
    ? Number(editData.position.id)
    : undefined
  const { billRate } = resolveEditorBillRate({
    isNew,
    originalPositionId,
    currentPositionId: shiftData.details?.positionId,
    shiftBillRate: shiftData.details?.billRate,
    positionBillRate: position?.billRate,
  })
  const isBillRate = billRate != null
  const formattedPay =
    typeof pay === 'number'
      ? isBillRate
        ? '$XX'
        : formatPreviewAmount(pay)
      : null
  const workDate = shiftData.schedule?.selectedDays?.[0]
  const startTimeLabel = shiftData.schedule?.startTime?.label
  const startTimeValue = shiftData.schedule?.startTime?.value
  const endTimeLabel = shiftData.schedule?.endTime?.label
  const endTimeValue = shiftData.schedule?.endTime?.value
  const timezone = shiftData.schedule?.timezone
  const hasDateTime = Boolean(workDate && startTimeLabel && endTimeLabel)
  const hasEstimatedPay =
    !isBillRate && Number.isFinite(estimatedPay) && estimatedPay > 0

  return (
    <ShiftHero>
      <CompanyLogo
        style={{
          backgroundImage: company?.logoUrl
            ? `url(${company.logoUrl})`
            : undefined,
        }}
      />
      {position?.name ? <PositionName>{position.name}</PositionName> : null}
      {company?.name ? <WorkplaceName>{company.name}</WorkplaceName> : null}
      {isHourly && formattedPay ? <ShiftPay>{formattedPay}/hr</ShiftPay> : null}
      {hasEstimatedPay ? (
        <EstimatedPay>
          <EstimatedPayText>
            Estimated Total {formatPreviewAmount(estimatedPay)}
          </EstimatedPayText>
        </EstimatedPay>
      ) : null}
      {isOnDemandDeliveryBlock ? (
        <OnDemandDeliverySchedule
          date={workDate}
          startTimeLabel={startTimeLabel}
          startTimeValue={startTimeValue}
          endTimeValue={endTimeValue}
          timezone={timezone}
        />
      ) : hasDateTime && workDate && startTimeLabel && endTimeLabel ? (
        <DateTimePill>
          <CalendarIcon size={14} aria-hidden="true" />
          <DateTimeText>{format(workDate, 'EEE MMM d')}</DateTimeText>
          <ClockIcon size={14} aria-hidden="true" />
          <DateTimeText>
            {startTimeLabel} - {endTimeLabel}
          </DateTimeText>
        </DateTimePill>
      ) : null}
      {shiftData.schedule?.isW2 ? <EmploymentBadge>W-2</EmploymentBadge> : null}
    </ShiftHero>
  )
}
